// Holdings screen view model. Pure: snapshot in, rows / summary / CSV out.
// Money is in dollars (matches PortfolioSnapshot). Percentages are 0..1.
import type { PortfolioSnapshot, PortfolioHolding, PortfolioAccount } from './api.js';
import { assetClassLabel } from './portfolio.js';

export type HoldingsView = 'account' | 'consolidated' | 'class';

export interface HoldingRow {
  key: string;
  code: string;
  name: string;
  assetClass: string;
  sector: string | null;
  securityType: string | null;
  accountIds: string[];
  accountNames: string[];
  units: number;
  price: number;
  value: number;
  costBase: number;
  gain: number;
  returnPct: number;
  /** value / household total (including cash). */
  weight: number;
  incomeTtm: number;
  frankingTtm: number;
  /** incomeTtm / value, or null when there is no income on record. */
  yieldPct: number | null;
  purchaseDate: string | null;
  holding: PortfolioHolding | null;
}

export interface HoldingsSummary {
  total: number;
  invested: number;
  cash: number;
  costBase: number;
  gain: number;
  returnPct: number;
  incomeTtm: number;
  frankingTtm: number;
  yieldPct: number;
  grossedUpYieldPct: number;
  positions: number;
  securities: number;
  accounts: number;
  oldestPriceAsOf: string | null;
  losers: number;
}

const GICS: Record<string, string> = {
  'GICS:10': 'Energy', 'GICS:15': 'Materials', 'GICS:20': 'Industrials', 'GICS:25': 'Consumer Discretionary',
  'GICS:30': 'Consumer Staples', 'GICS:35': 'Health Care', 'GICS:40': 'Financials', 'GICS:45': 'Information Technology',
  'GICS:50': 'Communication Services', 'GICS:55': 'Utilities', 'GICS:60': 'Real Estate',
};
export function sectorLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  return GICS[code] ?? code;
}

const TYPE_LABEL: Record<string, string> = {
  direct_equity: 'Share', etf: 'ETF', lic: 'LIC', hybrid: 'Hybrid', managed_fund: 'Managed fund', sma: 'SMA', cash: 'Cash',
};
export function securityTypeLabel(t: string | null | undefined): string | null {
  return t ? TYPE_LABEL[t] ?? t : null;
}

const ACCOUNT_LABEL: Record<string, string> = {
  personal: 'Personal', super: 'Superannuation', pension: 'Account-based pension', trust: 'Family trust',
  smsf: 'Self-managed super fund', company: 'Company',
};
export function accountTypeLabel(t: string | null | undefined): string {
  return t ? ACCOUNT_LABEL[t] ?? t : 'Account';
}

function row(h: PortfolioHolding, acct: PortfolioAccount, total: number): HoldingRow {
  const income = h.incomeTtm ?? 0;
  return {
    key: `${acct.id}:${h.code}`,
    code: h.code, name: h.name, assetClass: h.assetClass, sector: h.sector, securityType: h.securityType ?? null,
    accountIds: [acct.id], accountNames: [acct.name],
    units: h.units, price: h.currentPrice, value: h.currentValue, costBase: h.costBase,
    gain: h.unrealisedGainLoss, returnPct: h.unrealisedReturnPct,
    weight: total > 0 ? h.currentValue / total : 0,
    incomeTtm: income, frankingTtm: h.frankingTtm ?? 0,
    yieldPct: income > 0 && h.currentValue > 0 ? income / h.currentValue : null,
    purchaseDate: h.purchaseDate ?? null,
    holding: h,
  };
}

/** One row per holding per account. */
export function accountRows(p: PortfolioSnapshot, acct: PortfolioAccount): HoldingRow[] {
  return acct.holdings.map((h) => row(h, acct, p.tnw));
}

/** One row per security across the household. */
export function consolidatedRows(p: PortfolioSnapshot): HoldingRow[] {
  const by = new Map<string, HoldingRow>();
  for (const a of p.accounts) {
    for (const h of a.holdings) {
      const r = row(h, a, p.tnw);
      const cur = by.get(h.code);
      if (!cur) { by.set(h.code, { ...r, key: h.code }); continue; }
      cur.accountIds.push(a.id); cur.accountNames.push(a.name);
      cur.units += r.units; cur.value += r.value; cur.costBase += r.costBase; cur.gain += r.gain;
      cur.incomeTtm += r.incomeTtm; cur.frankingTtm += r.frankingTtm;
      if (r.purchaseDate && (!cur.purchaseDate || r.purchaseDate < cur.purchaseDate)) cur.purchaseDate = r.purchaseDate;
      cur.holding = null;
    }
  }
  for (const r of by.values()) {
    r.weight = p.tnw > 0 ? r.value / p.tnw : 0;
    r.returnPct = r.costBase > 0 ? r.gain / r.costBase : 0;
    r.price = r.units > 0 ? r.value / r.units : r.price;
    r.yieldPct = r.incomeTtm > 0 && r.value > 0 ? r.incomeTtm / r.value : null;
  }
  return [...by.values()];
}

export type SortKey = 'code' | 'class' | 'units' | 'price' | 'value' | 'weight' | 'costBase' | 'gain' | 'returnPct' | 'income' | 'yield';
export interface Sort { key: SortKey; dir: 'asc' | 'desc' }

export function sortRows(rows: HoldingRow[], s: Sort): HoldingRow[] {
  const get = (r: HoldingRow): number | string => {
    switch (s.key) {
      case 'code': return r.code;
      case 'class': return assetClassLabel(r.assetClass);
      case 'units': return r.units;
      case 'price': return r.price;
      case 'value': return r.value;
      case 'weight': return r.weight;
      case 'costBase': return r.costBase;
      case 'gain': return r.gain;
      case 'returnPct': return r.returnPct;
      case 'income': return r.incomeTtm;
      case 'yield': return r.yieldPct ?? -1;
    }
  };
  const mul = s.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = get(a); const y = get(b);
    const c = typeof x === 'string' || typeof y === 'string' ? String(x).localeCompare(String(y)) : x - y;
    return c * mul || a.code.localeCompare(b.code);
  });
}

export function filterRows(rows: HoldingRow[], q: string): HoldingRow[] {
  const t = q.trim().toLowerCase();
  if (!t) return rows;
  return rows.filter((r) => r.code.toLowerCase().includes(t) || r.name.toLowerCase().includes(t)
    || assetClassLabel(r.assetClass).toLowerCase().includes(t) || (sectorLabel(r.sector) ?? '').toLowerCase().includes(t));
}

export interface ClassGroup { assetClass: string; label: string; rows: HoldingRow[]; value: number; weight: number; gain: number; income: number }

/** Rows grouped by asset class, largest class first. */
export function groupByClass(rows: HoldingRow[], total: number): ClassGroup[] {
  const m = new Map<string, HoldingRow[]>();
  for (const r of rows) m.set(r.assetClass, [...(m.get(r.assetClass) ?? []), r]);
  return [...m.entries()].map(([k, rs]) => {
    const value = rs.reduce((s, r) => s + r.value, 0);
    return {
      assetClass: k, label: assetClassLabel(k), rows: rs, value, weight: total > 0 ? value / total : 0,
      gain: rs.reduce((s, r) => s + r.gain, 0), income: rs.reduce((s, r) => s + r.incomeTtm, 0),
    };
  }).sort((a, b) => b.value - a.value);
}

export function summarise(p: PortfolioSnapshot): HoldingsSummary {
  const hs = p.accounts.flatMap((a) => a.holdings);
  const invested = hs.reduce((s, h) => s + h.currentValue, 0);
  const costBase = hs.reduce((s, h) => s + h.costBase, 0);
  const gain = hs.reduce((s, h) => s + h.unrealisedGainLoss, 0);
  const incomeTtm = hs.reduce((s, h) => s + (h.incomeTtm ?? 0), 0);
  const frankingTtm = hs.reduce((s, h) => s + (h.frankingTtm ?? 0), 0);
  const asOfs = hs.map((h) => h.priceAsOf).filter((d): d is string => !!d).sort();
  return {
    total: p.tnw, invested, cash: p.cash, costBase, gain,
    returnPct: costBase > 0 ? gain / costBase : 0,
    incomeTtm, frankingTtm,
    yieldPct: invested > 0 ? incomeTtm / invested : 0,
    grossedUpYieldPct: invested > 0 ? (incomeTtm + frankingTtm) / invested : 0,
    positions: hs.length,
    securities: new Set(hs.map((h) => h.code)).size,
    accounts: p.accounts.length,
    oldestPriceAsOf: asOfs[0] ?? null,
    losers: hs.filter((h) => h.unrealisedGainLoss < 0).length,
  };
}

/* ---------------- Allocation vs target ---------------- */

export interface TargetInput {
  targetAllocAu?: string | number | null; targetAllocIntl?: string | number | null; targetAllocProperty?: string | number | null;
  targetAllocAlt?: string | number | null; targetAllocFi?: string | number | null; targetAllocCash?: string | number | null;
}

export interface AllocationRow { bucket: string; label: string; value: number; actual: number; target: number | null; drift: number | null }

const TARGET_KEY: Array<[string, keyof TargetInput]> = [
  ['equity_au', 'targetAllocAu'], ['equity_intl', 'targetAllocIntl'], ['property', 'targetAllocProperty'],
  ['alt', 'targetAllocAlt'], ['fi', 'targetAllocFi'], ['cash', 'targetAllocCash'],
];
const ORDER = ['equity_au', 'equity_intl', 'property', 'alt', 'fi', 'cash'];

/** Actual vs target by asset class. Target rows with 0 target and no holding are dropped. */
export function allocationVsTarget(p: PortfolioSnapshot, targets: TargetInput | null | undefined): { rows: AllocationRow[]; hasTargets: boolean } {
  const t = new Map<string, number>();
  let any = false;
  for (const [bucket, k] of TARGET_KEY) {
    const v = targets?.[k];
    if (v != null && v !== '' && !Number.isNaN(Number(v))) { t.set(bucket, Number(v)); any = any || Number(v) > 0; }
  }
  const actual = new Map(p.allocation.map((s) => [s.bucket, s]));
  const buckets = new Set<string>([...actual.keys(), ...(any ? [...t.keys()].filter((b) => (t.get(b) ?? 0) > 0) : [])]);
  const rows = [...buckets].map((b) => {
    const a = actual.get(b);
    const target = any ? t.get(b) ?? 0 : null;
    const pct = a?.pct ?? 0;
    return { bucket: b, label: assetClassLabel(b), value: a?.value ?? 0, actual: pct, target, drift: target == null ? null : pct - target };
  });
  rows.sort((x, y) => {
    const ix = ORDER.indexOf(x.bucket); const iy = ORDER.indexOf(y.bucket);
    return (ix < 0 ? 99 : ix) - (iy < 0 ? 99 : iy) || y.value - x.value;
  });
  return { rows, hasTargets: any };
}

/* ---------------- CSV ---------------- */

const csvCell = (v: string | number | null | undefined): string => {
  if (v == null) return '';
  const s = typeof v === 'number' ? String(Math.round(v * 100) / 100) : v;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function holdingsCsv(rows: HoldingRow[]): string {
  const head = ['Code', 'Security', 'Account', 'Asset class', 'Sector', 'Units', 'Price', 'Value', 'Weight %', 'Cost base', 'Unrealised gain', 'Return %', 'Income (12m)', 'Franking (12m)', 'Yield %', 'Purchased'];
  const body = rows.map((r) => [
    r.code, r.name, r.accountNames.join('; '), assetClassLabel(r.assetClass), sectorLabel(r.sector),
    r.units, r.price, r.value, r.weight * 100, r.costBase, r.gain, r.returnPct * 100,
    r.incomeTtm, r.frankingTtm, r.yieldPct == null ? null : r.yieldPct * 100, r.purchaseDate,
  ].map(csvCell).join(','));
  return [head.join(','), ...body].join('\n');
}
